"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Check, Lock, Sparkles } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/components/i18n-provider";
import { paymentTierAmount } from "@/types/payments";
import { formatPrice } from "@/components/offer/offer-catalog";
import { replacePercent } from "@/components/cv-form/payment-format";
import {
  buildBundleEntitlementLines,
  CAREER_BUNDLE_ANCHOR_AMOUNT,
  careerBundleSavingsPercent,
} from "@/lib/payments/career-bundle-catalog";
import { BundleVisual } from "@/components/offer/bundle-visual";
import { CareerBundleCheckoutDialog } from "@/components/offer/career-bundle-checkout-dialog";
import { trackEvent } from "@/lib/analytics";

function ChecklistColumn({
  label,
  items,
  tone,
}: {
  label: string;
  items: readonly string[];
  tone: "get" | "plus";
}) {
  return (
    <div className="space-y-2.5">
      <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">
        {label}
      </p>
      <ul className="space-y-2.5">
        {items.map((item) => (
          <li
            key={item}
            className="flex items-start gap-2 text-sm text-text-body"
          >
            {tone === "get" ? (
              <Check
                className="mt-0.5 size-4 flex-shrink-0 text-green-600"
                aria-hidden="true"
              />
            ) : (
              <Sparkles
                className="mt-0.5 size-4 flex-shrink-0 text-brand-primary"
                aria-hidden="true"
              />
            )}
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function CareerBundleHero({
  publicMode = false,
}: {
  publicMode?: boolean;
}) {
  const router = useRouter();
  const { locale, dictionary } = useI18n();
  const copy = dictionary.dashboard.offerPage.bundle;
  const bundleCopy = dictionary.payment.bundle;
  const price = paymentTierAmount["Career OS"];
  const anchorAmount = CAREER_BUNDLE_ANCHOR_AMOUNT;
  const savedAmount = Math.max(anchorAmount - price, 0);
  // Floored by the shared helper so the advertised saving is never rounded up.
  const savedPercent = careerBundleSavingsPercent(price);
  const entitlementLines = buildBundleEntitlementLines(
    bundleCopy.entitlementLabels,
  );

  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    trackEvent("offer_bundle_cta", {
      locale,
      source: publicMode ? "offer_public" : "offer_page",
    });

    // Checkout needs an authed session; public visitors sign up first and land
    // back on the offer page to complete the purchase.
    if (publicMode) {
      router.push(
        `/${locale}/sign-up?redirect=${encodeURIComponent(`/${locale}/dashboard/offer`)}`,
      );
      return;
    }

    setOpen(true);
  };

  return (
    <>
      <section className="overflow-hidden rounded-2xl border border-brand-primary/40 bg-brand-primary/5">
        <div className="grid gap-6 p-6 md:p-8 lg:grid-cols-[1fr_auto] lg:gap-8">
          <div className="space-y-5">
            <div className="space-y-2">
              <Badge className="gap-1 border-transparent bg-brand-primary text-white hover:bg-brand-primary">
                <Sparkles className="size-3" aria-hidden="true" />
                {copy.badge}
              </Badge>
              <h2 className="text-2xl font-bold text-text-heading">
                {copy.name}
              </h2>
              <p className="max-w-md text-sm leading-6 text-text-muted">
                {copy.tagline}
              </p>
            </div>

            <div className="grid gap-6 border-t border-brand-primary/15 pt-5 sm:grid-cols-2">
              <ChecklistColumn
                label={copy.includesLabel}
                items={entitlementLines}
                tone="get"
              />
              {bundleCopy.perks.length > 0 ? (
                <ChecklistColumn
                  label={copy.perksLabel}
                  items={bundleCopy.perks}
                  tone="plus"
                />
              ) : null}
            </div>
          </div>

          <div className="flex flex-col gap-4 lg:w-72">
            <div className="flex flex-col gap-3 rounded-2xl border border-border-primary bg-card p-5">
              <Badge className="w-fit border-transparent bg-green-100 text-green-700 hover:bg-green-100">
                {replacePercent(bundleCopy.save, savedPercent)}
              </Badge>
              <div className="space-y-1">
                <p className="text-sm text-text-muted line-through">
                  {formatPrice(anchorAmount)}
                </p>
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl font-bold text-brand-primary">
                    {formatPrice(price)}
                  </p>
                  <span className="text-xs text-text-muted">
                    {copy.priceSuffix}
                  </span>
                </div>
              </div>
              <p className="inline-flex w-fit rounded-full bg-green-100 px-3 py-1 text-sm font-semibold text-green-700">
                {copy.youSave} {formatPrice(savedAmount)} ({savedPercent}%)
              </p>
              <Button
                type="button"
                onClick={handleOpen}
                className="min-h-12 w-full bg-brand-primary text-base font-semibold text-white hover:bg-brand-primary-hover"
              >
                {copy.cta}
              </Button>
              <p className="flex items-center justify-center gap-1.5 text-xs text-text-muted">
                <Lock className="size-3" aria-hidden="true" />
                {copy.secure}
              </p>
            </div>

            <BundleVisual />
          </div>
        </div>
      </section>

      <CareerBundleCheckoutDialog open={open} onOpenChange={setOpen} />
    </>
  );
}
